import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://103.99.202.191:6020';
const API_BASE_URL = `${BACKEND_URL}/api`;

const apiService = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Attach bearer authorization token seamlessly from localStorage
apiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiService.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || 'Network request failed. Please check backend server.';
    return Promise.reject(new Error(message));
  }
);

// Admin Authentication, Profile & Password API Services
export const authService = {
  login: async (credentials) => {
    const res = await apiService.post('/admin/auth/login', credentials);
    const token = res.token || res.data?.token;
    if (token) {
      localStorage.setItem('adminToken', token);
      localStorage.setItem('token', token);
      if (res.user) {
        localStorage.setItem('user', JSON.stringify(res.user));
      }
    }
    return res;
  },

  forgotPassword: async (email) => {
    return await apiService.post('/admin/auth/forgot-password', { email });
  },

  verifyOTP: async (email, otp) => {
    return await apiService.post('/admin/auth/verify-otp', { email, otp });
  },

  resetPassword: async (email, otp, newPassword) => {
    return await apiService.post('/admin/auth/reset-password', { email, otp, newPassword });
  },

  updateProfile: async (name, email) => {
    return await apiService.put('/admin/profile', { name, email });
  },

  changePassword: async (currentPassword, newPassword) => {
    return await apiService.put('/admin/change-password', { currentPassword, newPassword });
  },

  getCurrentUser: async () => {
    return await apiService.get('/admin/auth/me');
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
};

// Admin Dashboard Analytics Service
export const dashboardService = {
  getStats: async () => {
    return await apiService.get('/admin/dashboard');
  },
};

// Admin Products & Panels API Services
export const productService = {
  getProducts: async () => {
    return await apiService.get('/admin/products');
  },
  addProduct: async (data) => {
    return await apiService.post('/admin/products', data);
  },
  updateProduct: async (id, data) => {
    return await apiService.put(`/admin/products/${id}`, data);
  },
  deleteProduct: async (id) => {
    return await apiService.delete(`/admin/products/${id}`);
  },
  updateStock: async (id, stock) => {
    return await apiService.patch(`/admin/inventory/${id}/stock`, { stock });
  },
  uploadImage: async (imageBase64, fileName) => {
    return await apiService.post('/admin/upload-image', { imageBase64, fileName });
  },
};

// Admin Store Settings API Services
export const settingsService = {
  getSettings: async () => {
    return await apiService.get('/admin/settings');
  },
  updateSettings: async (data) => {
    return await apiService.put('/admin/settings', data);
  },
};

// Admin Coupons & Offers API Services
export const couponService = {
  getCoupons: async () => {
    return await apiService.get('/admin/coupons');
  },
  getCouponUsages: async () => {
    return await apiService.get('/admin/coupons/usages');
  },
  createCoupon: async (data) => {
    return await apiService.post('/admin/coupons', data);
  },
  updateCoupon: async (id, data) => {
    return await apiService.put(`/admin/coupons/${id}`, data);
  },
  deleteCoupon: async (id) => {
    return await apiService.delete(`/admin/coupons/${id}`);
  },
  validateCoupon: async (code, orderAmount) => {
    return await apiService.post('/coupons/validate', { code, order_amount: orderAmount });
  },
};

// Admin Contact Messages API Services
export const contactService = {
  getMessages: async () => {
    return await apiService.get('/admin/contact-messages');
  },
  updateStatus: async (id, status) => {
    return await apiService.patch(`/admin/contact-messages/${id}/status`, { status });
  },
};

// Admin Orders API Services
export const orderService = {
  getOrders: async () => {
    return await apiService.get('/admin/orders');
  },
  updateStatus: async (id, status, extraData = {}) => {
    return await apiService.patch(`/admin/orders/${id}/status`, { order_status: status, ...extraData });
  },
};

// Admin Customers API Services
export const customerService = {
  getCustomers: async () => {
    return await apiService.get('/admin/customers');
  },
};

// Admin Product Reviews API Services
export const reviewService = {
  getReviews: async (productId = '') => {
    const url = productId ? `/admin/reviews?productId=${productId}` : '/admin/reviews';
    return await apiService.get(url);
  },
  softDeleteReview: async (id, isDeleted = true) => {
    return await apiService.patch(`/admin/reviews/${id}/soft-delete`, { is_deleted: isDeleted });
  },
};

// Admin Cancelled Orders & Refunds API Services
export const refundService = {
  getRefunds: async (status = 'ALL') => {
    return await apiService.get(`/admin/refunds?status=${status}`);
  },
  updateRefundStatus: async (refundId, refundStatus, notes = '') => {
    return await apiService.patch(`/admin/refunds/${refundId}/status`, { refund_status: refundStatus, notes });
  },
};

export default apiService;
